'use client';

import { HTTP_METHODS, useApiInvalidate, useApiMutation } from '@shared/api-utils';
import { Skeleton } from '@shared/components/shadcn';
import { useTranslations } from 'next-intl';
import { Children, type ComponentProps, type ReactNode, useState } from 'react';
import { PromotionsEmpty } from '..';
import { GraphicContainer } from '../../../GraphicContainer/GraphicContainer';
import { BonusInfoModal } from '../../../Offer/components/BonusInfoModal/BonusInfoModal';
import { GamesModal } from '../../../Offer/components/GamesModal/GamesModal';
import { PromotionPod, type PromotionPodProps } from '../../../PromotionPod/PromotionPod';
import type { ContentType } from '../../../types';
import type { PromoType } from '../../types/types';
import { getActiveUrlForInvalidation, getPromotionUrls } from '../../utils/promotionApiMapper';

type PromotionsListProps = {
  promoType: PromoType;
  data: Partial<ContentType>[];
  showTitle?: boolean;
  grayscale?: boolean;
};

type PromotionsContentProps = ComponentProps<'div'> & {
  promoType: PromoType;
  children?: ReactNode;
};

const classes = {
  list: 'flex flex-col gap-4 sm:flex-row sm:gap-6 sm:flex-wrap',
};

/**
 * Skeleton loader for the PromotionsContent component.
 * Displays placeholder cards while promotions data is loading.
 *
 * @param {ComponentProps<'div'>} props - Standard div props, including optional className.
 * @returns {JSX.Element} The skeleton UI for promotions content.
 */
function PromotionsContentSkeleton({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div className={className} {...props}>
      <div className={classes.list}>
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="w-full sm:w-102 border-border border rounded-md overflow-hidden flex flex-col max-h-100.25">
            <Skeleton className="h-38 w-full">
              <GraphicContainer className="bg-transparent h-full w-full" />
            </Skeleton>
            <div className="p-4 flex flex-col gap-4">
              <Skeleton className="h-5 w-1/2" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-20 w-full " />
              <Skeleton className="h-10 w-1/3 rounded-full" />
              <Skeleton className="h-5 w-full" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Promotion({ index, item, promoType, grayscale }: { index: number; item: Partial<ContentType>; promoType: PromoType; grayscale?: boolean }) {
  const { invalidateQuery } = useApiInvalidate();
  const [showGamesModal, setShowGamesModal] = useState(false);
  const [showMoreInfoModal, setShowMoreInfoModal] = useState(false);

  const urls = item.id ? getPromotionUrls(promoType, { itemId: item.id }) : null;

  const mutateClaim = useApiMutation(urls?.claim || '', promoType === 'free-spins' ? HTTP_METHODS.POST : HTTP_METHODS.PATCH, {
    requestOptions: {
      credentials: true,
    },
    mutationOptions: {
      onSuccess: () => {
        const invalidationUrl = getActiveUrlForInvalidation(promoType);
        if (invalidationUrl) {
          invalidateQuery(invalidationUrl);
        }
      },
    },
  });

  const handleClaim = () => {
    if (item.status === 'pending' && urls?.claim) {
      mutateClaim.mutate(undefined);
    }
  };

  const hasMultipleGames = item.bonusProductTypes?.includes('GAMES') && item.productIds && item.productIds.length > 1;

  const handleShowGames = () => {
    setShowGamesModal(true);
  };

  const handleShowMoreInfo = () => {
    setShowMoreInfoModal(true);
  };

  const promotion: PromotionPodProps = {
    data: item,
    variant: promoType,
    grayscale,
    className: 'sm:w-102',
    ...(item.status === 'pending' ? { ctaCallback: handleClaim } : hasMultipleGames ? { ctaCallback: handleShowGames } : {}),
    ...(item.moreInfoCtaModal ? { moreInfoCtaCallback: handleShowMoreInfo } : {}),
  };

  return (
    <>
      <PromotionPod key={index} {...promotion} />
      {hasMultipleGames && <GamesModal gamesNames={item.productIds!} isOpen={showGamesModal} onOpenChange={setShowGamesModal} data-track-container="Eligible Games" />}
      {item.moreInfoCtaModal && <BonusInfoModal isOpen={showMoreInfoModal} onOpenChange={setShowMoreInfoModal} data={item} />}
    </>
  );
}

/**
 * Renders a list of promotion cards using the PromotionPod component.
 *
 * @param {PromoType} props.promoType - The type of promotion (e.g., 'offers').
 * @param {ContentType[]} props.data - Array of promotion data to display.
 * @param {boolean} [props.showTitle=false] - Whether to show the section title.
 * @returns {JSX.Element} The rendered list of promotions.
 */
function PromotionsList({ promoType, data, showTitle = false, grayscale }: PromotionsListProps) {
  const t = useTranslations('offers.page');
  return (
    <>
      {showTitle ? <h4 className="my-4 sm:my-6">{t('previous_offers_title', { promoType: promoType === 'free-spins' ? 'freeSpins' : promoType })}</h4> : null}
      <section className={classes.list}>
        {data.map((item, index) => (
          <Promotion key={index} index={index} item={item} promoType={promoType} grayscale={grayscale} />
        ))}
      </section>
    </>
  );
}

/**
 * Renders the promotions content container. If no children are provided, it renders the PromotionsEmpty component.
 *
 * @param {PromoType} props.promoType - The type of promotion (e.g., 'offers').
 * @param {ReactNode} [props.children] - Optional children components.
 * @param {string} [props.className] - Additional class names.
 * @returns {JSX.Element} The rendered promotions content container.
 */
function PromotionsContent({ promoType, children, className, ...props }: PromotionsContentProps) {
  const hasChildren = Children.toArray(children).filter(Boolean).length > 0;
  return (
    <div className={className} {...props}>
      {hasChildren ? children : <PromotionsEmpty promoType={promoType} />}
    </div>
  );
}

export { PromotionsContent, PromotionsContentSkeleton, PromotionsList };
